import { useTranslator } from '@bluebeam/react-i18n';
import ConfirmModal from 'components/modals/ConfirmModal';
import React, { useState, useEffect } from 'react';
import { CreateRFI } from 'records/interactions/CreateRFI';
import { ModalState } from 'records/ModalState';
import * as toast from 'utils/toast';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import './CreateRFIOverlay.css';
import NarrowCreateRFIOverlay from './NarrowCreateRFIOverlay';
import WideCreateRFIOverlay from './WideCreateRFIOverlay';

interface Props extends WithAppStoreProps {
  interaction: CreateRFI;
}

const CreateRFIOverlay = (props: Props) => {
  const [creatingRFI, setCreatingRFI] = useState<boolean>(false);
  const [creatingText, setCreatingText] = useState<string>('');
  const { interaction, actions, appState } = props;
  useEffect(() => {
    actions.setViewRFI(interaction.rfi);
  }, [interaction.rfiid]);
  useEffect(() => {
    actions.setViewEdittedRFI(interaction.rfi);
  }, [interaction.rfi]);
  const { translate } = useTranslator(['rfiPage', 'buttons', 'toastText']);

  const handleSaveDraft = async (): Promise<void> => {
    const { interaction, actions } = props;
    try {
      setCreatingRFI(true);
      setCreatingText(translate('rfiPage:savingDraft'));

      const {
        rfi,
        files,
        removedAttachments,
        rfiid,
        projectDriveItems,
        integrationItems,
        studioItems,
        studioData,
        fromDraft,
      } = interaction;

      if (fromDraft) {
        await actions.updateRFIDraft(
          rfiid as string,
          rfi,
          files,
          removedAttachments,
          projectDriveItems,
          integrationItems,
          studioItems,
          studioData
        );
      } else {
        await actions.createRFIDraft(
          rfi,
          files,
          projectDriveItems,
          integrationItems,
          studioItems,
          studioData
        );
      }

      await actions.queryRFIs();
    } catch (err) {
      console.error('Unable to save RFI Draft: ', err);
      toast.error(
        translate('toastText:rfiDraftSaveError.message'),
        translate('toastText:rfiDraftSaveError.title')
      );
    } finally {
      actions.clearInteractions();
      setCreatingRFI(false);
    }
  };

  const handleDeleteRFIDraft = (evt: React.MouseEvent<HTMLElement>): void => {
    const { actions } = props;
    evt.stopPropagation();
    const modalState = new ModalState({
      id: 'launchDeleteRFIDraftDialog',
      portal: 'main',
      open: true,
      overlayClassName: 'modal__overlay',
      props: {
        headerText: translate('rfiPage:deleteRFIDraft'),
        acceptButtonText: translate('buttons:delete'),
        denyButtonText: translate('buttons:cancel'),
        acceptButtonType: 'delete',
        content: (
          <div className="column">
            <div>{translate('rfiPage:deleteDraftText')}</div>
            <div className="bold pt4">
              {translate('rfiPage:deleteDraftQuestion')}
            </div>
          </div>
        ),
        denyCallback: (): void => {
          actions.clearAllModals();
        },
        acceptCallback: (): void => {
          handleDeleteDraft();
        },
      },
      component: ConfirmModal,
    });

    actions.createModal(modalState);
  };

  const handleDeleteDraft = async (): Promise<void> => {
    const { interaction, actions } = props;
    const { rfiid, rfi } = interaction;
    try {
      await actions.deleteRFIDraft(rfiid as string, rfi.projectid);
      toast.success(
        translate('toastText:rfiDraftDeleted.message'),
        translate('toastText:rfiDraftDeleted.title')
      );
      await actions.queryRFIs();
    } catch (err) {
      console.error('Unable to delete RFI Draft: ', err);
      toast.error(
        translate('toastText:rfiDraftDeleteError.message'),
        translate('toastText:rfiDraftDeleteError.title')
      );
    } finally {
      actions.clearInteractions();
      actions.clearAllModals();
    }
  };

  const draftChangesModal = (): void => {
    const modalState = new ModalState({
      id: 'launchDraftChangesRFIDraftDialog',
      portal: 'main',
      open: true,
      overlayClassName: 'modal__overlay',
      props: {
        headerText: 'Are you sure?',
        content:
          'Unsaved changes will be lost if you cancel. Are you sure you wish to continue?',
        denyCallback: (): void => {
          props.actions.clearAllModals();
        },
        acceptCallback: (): void => {
          props.actions.clearAllModals();
          props.actions.clearInteractions();
        },
      },
      component: ConfirmModal,
    });
    props.actions.createModal(modalState);
  };

  const handleCancelClick = (): void => {
    if (interaction.rfi === appState.viewRFI) {
      props.actions.clearInteractions();
    } else draftChangesModal();
  };

  const handleCreateRFI = async (): Promise<void> => {
    const { interaction, actions } = props;
    try {
      setCreatingRFI(true);
      setCreatingText(translate('rfiPage:creatingRFI'));
      const {
        rfi,
        files,
        removedAttachments,
        rfiid,
        fromDraft,
        projectDriveItems,
        integrationItems,
        studioItems,
        studioData,
      } = interaction;

      if (fromDraft) {
        await actions.publishRFIDraft(
          rfiid as string,
          rfi,
          files,
          removedAttachments,
          projectDriveItems,
          integrationItems,
          studioItems,
          studioData
        );
      } else {
        await actions.createRFI(
          rfi,
          files,
          projectDriveItems,
          integrationItems,
          studioItems,
          studioData
        );
      }

      await actions.queryRFIsFromTable();
      await actions.getRFIs();
    } catch (err) {
      console.log('Unable to create RFI', err);
      toast.error(
        translate('toastText:rfiCreateError.message'),
        translate('toastText:rfiCreateError.title')
      );
    } finally {
      actions.clearInteractions();
      setCreatingRFI(false);
    }
  };

  const saveDisabled = (): boolean => {
    const {
      interaction: { rfi },
    } = props;

    return (
      rfi.title.length <= 0 ||
      rfi.question.length <= 0 ||
      rfi.assignees.size <= 0 ||
      rfi.submittingcompany.trim().length <= 0 ||
      rfi.submittingname.trim().length <= 0 ||
      rfi.submittingemail.trim().length <= 0 ||
      rfi.duedate <= 0
    );
  };

  const deleteDisabled = (): boolean => {
    const {
      interaction: { rfiid },
    } = props;

    return rfiid === null;
  };

  const handleToggleExpand = () => {
    const { interaction, actions } = props;
    const nextInteraction = interaction.update('expanded', (e) => !e);

    actions.updateInteraction(nextInteraction);
  };

  return interaction.expanded ? (
    <WideCreateRFIOverlay
      interaction={interaction}
      onCancelClick={handleCancelClick}
      onToggleExpand={handleToggleExpand}
      onCreateRFI={handleCreateRFI}
      onSaveDraft={handleSaveDraft}
      onDeleteDraft={handleDeleteRFIDraft}
      saveDisabled={saveDisabled()}
      deleteDisabled={deleteDisabled()}
    />
  ) : (
    <NarrowCreateRFIOverlay
      interaction={interaction}
      onCancelClick={handleCancelClick}
      onToggleExpand={handleToggleExpand}
      onCreateRFI={handleCreateRFI}
      onSaveDraft={handleSaveDraft}
      onDeleteDraft={handleDeleteRFIDraft}
      saveDisabled={saveDisabled()}
      deleteDisabled={deleteDisabled()}
      creatingRFI={creatingRFI}
      creatingText={creatingText}
    />
  );
};

export default withAppStore(CreateRFIOverlay);
